import { useMemo, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Globe2,
  Lock,
  MapPin,
  Plus,
  Share2,
  UserCheck,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getUserTimezone } from '../utils/datetime';
import {
  buildClubNameMap,
  formatViewerDay,
  formatViewerTime,
  getAgendaHealthMeta,
  getCalendarDay,
  getDayCount,
  getHostRoleLabel,
  getHostSessionActionMeta,
  getMonthGridDays,
  getNearbyMonthShortcuts,
  getOverflowCount,
  getSessionClubNames,
  getSessionState,
  getSessionsForDay,
  getTodayKey,
  getVisibleEventChips,
  groupSessionsByViewerDay,
  mergeHostRuns,
  shiftDayKey,
} from '../utils/hostSchedule';

const stateStyles = {
  active: 'border-zinc-200 bg-white hover:border-orange-300 hover:shadow-sm',
  cancelled: 'border-red-200 bg-red-50/70 opacity-90',
  completed: 'border-zinc-200 bg-zinc-50 opacity-80',
};

const stateBadgeStyles = {
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-zinc-200 text-zinc-700',
};

const chipStyles = {
  open: 'bg-blue-50 text-blue-700 border-blue-100',
  full: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  partial: 'bg-sky-50 text-sky-700 border-sky-100',
  waitlist: 'bg-purple-50 text-purple-700 border-purple-100',
  cancelled: 'bg-red-50 text-red-600 border-red-100 line-through opacity-75',
  completed: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const healthDotStyles = {
  open: 'bg-blue-500',
  full: 'bg-emerald-500',
  partial: 'bg-sky-500',
  waitlist: 'bg-purple-500',
  cancelled: 'bg-red-400',
  completed: 'bg-zinc-400',
};

const healthLabels = {
  open: 'Open',
  full: 'Full',
  partial: 'Partially filled',
  waitlist: 'Waitlist present',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export const SessionCard = ({
  session,
  clubNames,
  timezone,
  onOpenSession,
  onShareSession,
  onInviteSession,
  onViewPlayers,
  onCheckInSession,
  onDuplicateSession,
  onCancelSession,
}) => {
  const state = getSessionState(session);
  const health = getAgendaHealthMeta(session);
  const actions = getHostSessionActionMeta(session);
  const clubs = getSessionClubNames(session, clubNames);

  return (
    <article
      className={`w-full rounded-lg border p-4 transition ${stateStyles[state] || stateStyles.active}`}
      data-testid={`schedule-session-${session.id}`}
    >
      <button
        type="button"
        onClick={() => onOpenSession(session)}
        className="w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
        aria-label={`Open ${health.typeLabel.toLowerCase()} ${session.title || 'session'} at ${formatViewerTime(session.date_time, timezone)}`}
        data-testid={`schedule-session-details-${session.id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
            health.typeLabel === 'Training' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {health.typeLabel}
          </span>
          <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
            {getHostRoleLabel(session)}
          </span>
          {health.isFull && state === 'active' && (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Full
            </span>
          )}
          {state !== 'active' && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${stateBadgeStyles[state]}`}>
              {state === 'cancelled' ? 'Cancelled' : 'Completed'}
            </span>
          )}
          {actions.canShare && (
            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
              {session.is_private ? <Lock className="h-3 w-3" /> : <Globe2 className="h-3 w-3" />}
              {actions.privacyLabel}
            </span>
          )}
        </div>

        <h3 className="mt-2 break-words text-base font-semibold text-zinc-900">
          {session.title || 'Untitled session'}
        </h3>

        <div className="mt-3 flex flex-col gap-2 text-sm text-zinc-600 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4 flex-shrink-0" />
            {formatViewerTime(session.date_time, timezone)}
          </span>
          {clubs.length > 0 && (
            <span className="break-words text-zinc-700">{clubs.join(', ')}</span>
          )}
          {session.location_name && (
            <span className="flex min-w-0 items-center gap-1.5">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{session.location_name}</span>
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-zinc-700">
          <span className="flex items-center gap-1.5 font-medium">
            <Users className="h-4 w-4" />
            {health.joinedCapacity} players
          </span>
          {health.spotsLabel && (
            <span className="text-zinc-500">
              {health.spotsLabel}
            </span>
          )}
          {health.waitlistLabel && (
            <span className="text-zinc-500">
              {health.waitlistLabel}
            </span>
          )}
        </div>
        {health.fillPercent !== null && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-100" aria-label={`${health.joinedCapacity} players`}>
            <div
              className={`h-full rounded-full ${
                state === 'cancelled' || state === 'completed'
                  ? 'bg-zinc-300'
                  : health.isFull
                    ? 'bg-emerald-500'
                    : 'bg-orange-400'
              }`}
              style={{ width: `${health.fillPercent}%` }}
            />
          </div>
        )}
      </button>

      {(actions.canShare || actions.canCheckIn) && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-100 pt-3">
          {actions.canCheckIn && (
            <Button
              type="button"
              size="sm"
              onClick={() => onCheckInSession?.(session)}
              className="bg-orange-500 text-white hover:bg-orange-600"
              data-testid={`schedule-check-in-${session.id}`}
            >
              <UserCheck className="mr-1.5 h-4 w-4" />
              Check In
            </Button>
          )}
          {actions.canShare && (
            <>
              <Button
                type="button"
                size="sm"
                onClick={() => onShareSession?.(session)}
                className="bg-orange-500 text-white hover:bg-orange-600"
                data-testid={`schedule-share-${session.id}`}
              >
                <Share2 className="mr-1.5 h-4 w-4" />
                Share
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onInviteSession?.(session)}
                disabled={!actions.canInvite}
                title={actions.canInvite ? 'Invite players' : 'This session is full'}
                data-testid={`schedule-invite-${session.id}`}
              >
                <UserPlus className="mr-1.5 h-4 w-4" />
                Invite
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onViewPlayers?.(session)}
            data-testid={`schedule-view-players-${session.id}`}
          >
            <Users className="mr-1.5 h-4 w-4" />
            View Players
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onDuplicateSession?.(session)}
            data-testid={`schedule-duplicate-${session.id}`}
          >
            <Copy className="mr-1.5 h-4 w-4" />
            Duplicate Game
          </Button>
        </div>
      )}
      {actions.canCancel && (
        <div className="mt-3 border-t border-red-100 pt-3">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onCancelSession?.(session)}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid={`schedule-cancel-${session.id}`}
          >
            <XCircle className="mr-1.5 h-4 w-4" />
            Cancel Game
          </Button>
        </div>
      )}
    </article>
  );
};

const DayAgenda = ({
  dayKey,
  sessions,
  clubNames,
  timezone,
  onOpenSession,
  onShareSession,
  onInviteSession,
  onViewPlayers,
  onCheckInSession,
  onDuplicateSession,
  onCancelSession,
}) => (
  <section className="space-y-3" data-testid={`schedule-day-${dayKey}`}>
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
        {formatViewerDay(dayKey, timezone)}
      </h3>
      <p className="text-sm text-zinc-500">
        {sessions.length} session{sessions.length === 1 ? '' : 's'}
      </p>
    </div>
    <div className="space-y-3">
      {sessions.map((session) => (
        <SessionCard
          key={session.id}
          session={session}
          clubNames={clubNames}
          timezone={timezone}
          onOpenSession={onOpenSession}
          onShareSession={onShareSession}
          onInviteSession={onInviteSession}
          onViewPlayers={onViewPlayers}
          onCheckInSession={onCheckInSession}
          onDuplicateSession={onDuplicateSession}
          onCancelSession={onCancelSession}
        />
      ))}
    </div>
  </section>
);

const EventChip = ({ chip }) => (
  <span
    className={`flex min-w-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium leading-tight ${chipStyles[chip.factualState] || chipStyles.open}`}
    title={`${chip.typeLabel}: ${chip.label} - ${healthLabels[chip.factualState] || 'Open'} - ${chip.joinedCapacity}`}
  >
    <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${healthDotStyles[chip.factualState] || healthDotStyles.open}`} />
    <span className="flex-shrink-0 text-[9px] font-bold uppercase leading-none">
      {chip.typeLabel === 'Training' ? 'T' : 'R'}
    </span>
    <span className="min-w-0 truncate">{chip.label}</span>
  </span>
);

const MobileMonthDay = ({
  day,
  sessions,
  todayKey,
  selectedDayKey,
  onSelect,
}) => {
  const selected = day.key === selectedDayKey;
  const isToday = day.key === todayKey;
  const chips = getVisibleEventChips(sessions, 2, 13);
  const overflowCount = getOverflowCount(sessions, 2);

  return (
    <button
      type="button"
      onClick={() => onSelect(day.key)}
      className={`min-w-0 rounded-lg border p-1 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 ${
        selected
          ? 'border-orange-500 bg-orange-50 shadow-sm'
          : 'border-zinc-200 bg-white hover:border-orange-300'
      } ${day.isCurrentMonth ? '' : 'bg-zinc-50 text-zinc-400'}`}
      aria-pressed={selected}
      aria-label={`${day.key}${sessions.length ? `, ${sessions.length} session${sessions.length === 1 ? '' : 's'}` : ''}`}
      data-testid={`mobile-month-day-${day.key}`}
    >
      <span
        className={`mb-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${
          selected
            ? 'bg-orange-500 text-white'
            : isToday
              ? 'bg-zinc-900 text-white'
              : day.isCurrentMonth
                ? 'text-zinc-700'
                : 'text-zinc-400'
        }`}
      >
        {day.dayNumber}
      </span>
      <span className="flex min-h-10 flex-col gap-0.5 overflow-hidden">
        {chips.map((chip) => (
          <EventChip key={chip.id} chip={chip} />
        ))}
        {overflowCount > 0 && (
          <span className="truncate text-[10px] font-semibold leading-tight text-zinc-500">
            +{overflowCount}
          </span>
        )}
      </span>
    </button>
  );
};

const HostSchedule = ({
  runs,
  clubs,
  onOpenSession,
  onCreateSession,
  onShareSession,
  onInviteSession,
  onViewPlayers,
  onCheckInSession,
  onDuplicateSession,
  onCancelSession,
}) => {
  const timezone = getUserTimezone();
  const todayKey = useMemo(() => getTodayKey(timezone), [timezone]);
  const [selectedDayKey, setSelectedDayKey] = useState(todayKey);
  const [monthDayKey, setMonthDayKey] = useState(todayKey);

  const sessions = useMemo(() => mergeHostRuns(runs), [runs]);
  const clubNames = useMemo(() => buildClubNameMap(clubs), [clubs]);
  const groupedSessions = useMemo(
    () => groupSessionsByViewerDay(sessions, timezone),
    [sessions, timezone]
  );
  const monthDays = useMemo(
    () => getMonthGridDays(monthDayKey, timezone),
    [monthDayKey, timezone]
  );
  const monthShortcuts = useMemo(
    () => getNearbyMonthShortcuts(monthDayKey, timezone, 5),
    [monthDayKey, timezone]
  );

  const selectedDaySessions = getSessionsForDay(groupedSessions, selectedDayKey);
  const monthLabel = getCalendarDay(monthDayKey, timezone).format('MMMM YYYY');

  const setMonth = (amount) => {
    const nextMonthKey = shiftDayKey(monthDayKey, amount, 'month', timezone);
    const firstOfMonth = getCalendarDay(nextMonthKey, timezone).startOf('month').format('YYYY-MM-DD');
    setMonthDayKey(firstOfMonth);
    setSelectedDayKey(firstOfMonth);
  };

  const selectToday = () => {
    setSelectedDayKey(todayKey);
    setMonthDayKey(todayKey);
  };

  const selectMonthShortcut = (dayKey) => {
    setMonthDayKey(dayKey);
    setSelectedDayKey(dayKey);
  };

  const selectMobileDay = (dayKey) => {
    setSelectedDayKey(dayKey);
    setMonthDayKey(dayKey);
  };

  if (sessions.length === 0) {
    return (
      <div>
        <Card className="p-8 text-center" data-testid="host-schedule-empty">
          <CalendarDays className="mx-auto mb-3 h-12 w-12 text-zinc-300" />
          <p className="font-medium text-zinc-700">Publish your first session</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
            Create a run or training, then share its link or QR code with your players.
          </p>
          {onCreateSession && (
            <Button
              type="button"
              onClick={onCreateSession}
              className="mt-5 bg-orange-500 hover:bg-orange-600"
              data-testid="empty-schedule-create-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create First Session
            </Button>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="host-schedule">
      <div className="relative space-y-4 pb-20 lg:hidden">
        <div className="mb-3 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMonth(-1)}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 text-center">
            <h2 className="truncate text-lg font-bold text-zinc-900">{monthLabel}</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={selectToday}
              className="h-7 text-xs focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1"
              aria-label="Go to today"
            >
              Today
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMonth(1)}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" data-testid="mobile-month-shortcuts">
          {monthShortcuts.map((month) => {
            const selectedMonth = getCalendarDay(monthDayKey, timezone).format('YYYY-MM') === getCalendarDay(month.key, timezone).format('YYYY-MM');
            return (
              <button
                key={month.key}
                type="button"
                onClick={() => selectMonthShortcut(month.key)}
                className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 ${
                  selectedMonth ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-zinc-200 bg-white text-zinc-600'
                }`}
                aria-pressed={selectedMonth}
                aria-label={`Show ${month.fullLabel}`}
              >
                {month.label}
              </button>
            );
          })}
        </div>

        <Card className="overflow-hidden p-2" data-testid="mobile-month-grid">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-zinc-500">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="py-1">{label}</div>
            ))}
          </div>
          <div className="grid min-w-0 grid-cols-7 gap-1">
            {monthDays.map((day) => (
              <MobileMonthDay
                key={day.key}
                day={day}
                sessions={getSessionsForDay(groupedSessions, day.key)}
                todayKey={todayKey}
                selectedDayKey={selectedDayKey}
                onSelect={selectMobileDay}
              />
            ))}
          </div>
        </Card>

        <div className="space-y-6" data-testid="schedule-mobile-agenda">
          {selectedDaySessions.length === 0 ? (
            <Card className="p-6 text-center text-sm text-zinc-500">
              No sessions on {formatViewerDay(selectedDayKey, timezone)}
            </Card>
          ) : (
            <DayAgenda
              dayKey={selectedDayKey}
              sessions={selectedDaySessions}
              clubNames={clubNames}
              timezone={timezone}
              onOpenSession={onOpenSession}
              onShareSession={onShareSession}
              onInviteSession={onInviteSession}
              onViewPlayers={onViewPlayers}
              onCheckInSession={onCheckInSession}
              onDuplicateSession={onDuplicateSession}
              onCancelSession={onCancelSession}
            />
          )}
        </div>

        {onCreateSession && (
          <Button
            type="button"
            onClick={onCreateSession}
            className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-20 h-12 w-12 rounded-full bg-orange-500 p-0 shadow-lg hover:bg-orange-600"
            aria-label="Create session"
            data-testid="mobile-schedule-create-btn"
          >
            <Plus className="h-5 w-5" />
          </Button>
        )}
      </div>

      <div className="hidden gap-6 lg:grid lg:grid-cols-[minmax(300px,0.85fr)_minmax(0,1.15fr)]">
        <Card className="p-4" data-testid="schedule-month-selector">
          <div className="mb-4 flex items-center justify-between">
            <Button type="button" variant="outline" size="sm" onClick={() => setMonth(-1)} aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center">
              <h2 className="font-semibold text-zinc-900">{monthLabel}</h2>
              <Button type="button" variant="ghost" size="sm" onClick={selectToday} className="mt-1 h-7 text-xs">
                Today
              </Button>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setMonth(1)} aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium uppercase text-zinc-500">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="py-1">{label}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthDays.map((day) => {
              const count = getDayCount(groupedSessions, day.key);
              const selected = day.key === selectedDayKey;
              const isToday = day.key === todayKey;
              return (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => {
                    setSelectedDayKey(day.key);
                    setMonthDayKey(day.key);
                  }}
                  className={`relative min-h-16 rounded-lg border p-2 text-left transition ${
                    selected
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:border-orange-300'
                  } ${day.isCurrentMonth ? '' : 'opacity-45'}`}
                  aria-pressed={selected}
                  data-testid={`schedule-month-day-${day.key}`}
                >
                  <span className={`text-sm font-semibold ${isToday ? 'text-orange-600' : ''}`}>
                    {day.dayNumber}
                  </span>
                  {count > 0 && (
                    <span className="absolute bottom-2 right-2 rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        <div className="min-w-0">
          {selectedDaySessions.length === 0 ? (
            <Card className="p-8 text-center text-zinc-500" data-testid="schedule-selected-empty">
              No sessions on {formatViewerDay(selectedDayKey, timezone)}
            </Card>
          ) : (
            <DayAgenda
              dayKey={selectedDayKey}
              sessions={selectedDaySessions}
              clubNames={clubNames}
              timezone={timezone}
              onOpenSession={onOpenSession}
              onShareSession={onShareSession}
              onInviteSession={onInviteSession}
              onViewPlayers={onViewPlayers}
              onCheckInSession={onCheckInSession}
              onDuplicateSession={onDuplicateSession}
              onCancelSession={onCancelSession}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HostSchedule;
